import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner, UserCellProps } from '../../components/Molecules';
import {
  AboutCashCreditPopup,
  AboutJOYCashPopup,
  DistributorReferralRewardsPopup,
} from '../../components/Popups';
import { WebStoreReferralRewardTemplate } from '../../components/Templates';
import {
  DEFAULT_DISTRIBUTORID,
  DEFAULT_PARTNERID,
} from '../../constants/appInfo';
import { cashOutRequirements } from '../../constants/exchangeRates';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { ReferralSystem, RewardReferralRole } from '../../models/RewardStore';
import { Profile } from '../../models/UserStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

type State = {
  loadingTotal: number;
  userRewardReferralCode: string | undefined;
};

const { fetchRewardReferral } = useRewardStore.getState();
const { getCurrentUser } = useUserStore.getState();

const WebStoreReferralRewardScreen = () => {
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  const userAccount = useUserStore((store) => store.getAccount(userId ?? ''));

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? 'USD';
  }, [userProfile]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    userRewardReferralCode: undefined,
  });

  useRedirectEntry();

  const getReferralCode = useCallback(async () => {
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
    const referralCodeData = await useRewardStore
      .getState()
      .getRewardReferralCode(ReferralSystem.Joyajoy);
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    if (referralCodeData) {
      setState((s) => ({
        ...s,
        userRewardReferralCode: referralCodeData.referralCode,
      }));
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getReferralCode().catch(console.error);
    }
  }, [getReferralCode, userId]);

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [userId]);

  const userRewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  const referredByProfile = useUserStore((store) =>
    store.getProfile(userRewardReferral?.referredBy ?? ''),
  );

  const rewardReferralFieldName = `${userId}_all`;
  const rewardReferralField = useRewardStoreListField(
    'referrals',
    rewardReferralFieldName,
  );

  const fetchRewardReferrals = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore.getState().fetchRewardReferrals(
        {
          referralSystem: ReferralSystem.Joyajoy,
          fieldName: rewardReferralFieldName,
        },
        cursor,
      ),
    [rewardReferralFieldName],
  );

  const { fetchAll: fetchAllRewardReferrals } = useFetchList(
    rewardReferralField,
    fetchRewardReferrals,
  );

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      getCurrentUser().catch(console.error);
      fetchAllRewardReferrals()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [fetchAllRewardReferrals, userId]);

  const rewardReferrals = useRewardStore((store) => {
    return store.getRewardReferrals(rewardReferralFieldName);
  }, arrayEqual(shallow));

  const onClickReferral = useCallback(
    (
      profile: Profile,
      userRole: RewardReferralRole,
      referralRole: RewardReferralRole,
    ) => {
      if (
        userRole === RewardReferralRole.Partner &&
        referralRole === RewardReferralRole.Distributor
      ) {
        window.scrollTo(0, 0);
        navigate(`/store/referral/distributor/${profile.userId}`);
      } else if (
        userRole !== RewardReferralRole.Partner &&
        referralRole === RewardReferralRole.Customer
      ) {
        window.scrollTo(0, 0);
        navigate(`/store/referral/customer/${profile.userId}`);
      }
    },
    [navigate],
  );

  const convertReferralsToUserCellData = useCallback(
    (referralRole: RewardReferralRole) => {
      return (
        rewardReferrals
          .filter((referral) => referral.role === referralRole)
          .slice(0, 5)
          .map((referral) => {
            const referralProfile = useUserStore
              .getState()
              .getProfile(referral?.userId ?? '');
            const purchasedAt =
              referralRole === RewardReferralRole.Customer
                ? undefined
                : referral.referredAt;

            const confirmManageReward =
              userRewardReferral?.role === RewardReferralRole.Partner &&
              referralRole === RewardReferralRole.Distributor
                ? referral.managementReward?.confirmed.convertToCurrency(
                    currency,
                  )
                : undefined;
            const referralCount =
              userRewardReferral?.role === RewardReferralRole.Partner &&
              referralRole === RewardReferralRole.Distributor
                ? referralProfile?.statistics.joyajoyCustomerReferralCount
                : referralRole === RewardReferralRole.Customer
                ? referralProfile?.totalJoyajoyReferralCount
                : undefined;
            if (referralProfile && userRewardReferral) {
              const confirmReward = referral
                .getConfirmReward(userRewardReferral?.role)
                ?.convertToCurrency(currency);
              return {
                profile: referralProfile,
                purchasedAt: purchasedAt,
                confirmReward: confirmReward
                  ? `${currency}${confirmReward}`
                  : undefined,
                confirmManageReward: confirmManageReward
                  ? `${currency}${confirmManageReward}`
                  : undefined,
                referralCount: referralCount,
                hasCreditIcon:
                  userRewardReferral?.role === RewardReferralRole.Customer,
                onClickReferral:
                  referralCount !== undefined
                    ? () => {
                        onClickReferral(
                          referralProfile,
                          userRewardReferral.role,
                          referral.role,
                        );
                      }
                    : undefined,
              };
            } else {
              return null;
            }
          }) ?? []
      ).filter(Boolean) as UserCellProps[];
    },
    [currency, onClickReferral, rewardReferrals, userRewardReferral],
  );

  const referralCustomerData = useMemo<UserCellProps[]>(() => {
    return convertReferralsToUserCellData(RewardReferralRole.Customer);
  }, [convertReferralsToUserCellData]);

  const referralDistributorData = useMemo<UserCellProps[]>(() => {
    return convertReferralsToUserCellData(RewardReferralRole.Distributor);
  }, [convertReferralsToUserCellData]);

  const referralPartnerData = useMemo<UserCellProps[]>(() => {
    return convertReferralsToUserCellData(RewardReferralRole.Partner);
  }, [convertReferralsToUserCellData]);

  const onClickCashOut = useCallback(() => {
    if (userRewardReferral?.isPartnerOrDistributorRole) {
      // TODO: show Cash out popup
    } else {
      navigate('/store');
    }
  }, [navigate, userRewardReferral?.isPartnerOrDistributorRole]);

  const onClickRoleDetail = useCallback(() => {
    navigate('/store/role');
  }, [navigate]);

  const onClickJOYCashInfoPopup = useCallback(() => {
    if (userRewardReferral?.isPartnerOrDistributorRole) {
      showPopup(
        <AboutJOYCashPopup
          cashOutRequirement={`${currency}${numbro(
            cashOutRequirements[currency],
          ).format({ thousandSeparated: true })}`}
          onPrimary={hidePopup}
        />,
      );
    } else {
      showPopup(<AboutCashCreditPopup onPrimary={hidePopup} />);
    }
  }, [
    currency,
    hidePopup,
    showPopup,
    userRewardReferral?.isPartnerOrDistributorRole,
  ]);

  const onClickAll = useCallback(
    (role: RewardReferralRole) => {
      navigate(`/store/referral/${role}`);
    },
    [navigate],
  );

  const viewPopupDetails = useCallback(() => {
    switch (userRewardReferral?.role) {
      case RewardReferralRole.Distributor:
        navigate(`/store/${DEFAULT_DISTRIBUTORID}`);
        break;
      case RewardReferralRole.Partner:
        navigate(`/store/${DEFAULT_PARTNERID}`);
        break;
      default:
        navigate(`/store/referral/customer/detail`);
        break;
    }
  }, [userRewardReferral?.role, navigate]);

  const onClickReferralPopup = useCallback(() => {
    showPopup(
      <DistributorReferralRewardsPopup
        role={userRewardReferral?.role ?? RewardReferralRole.Customer}
        onPrimary={hidePopup}
        onClickViewDetails={() => {
          hidePopup();
          viewPopupDetails();
        }}
      />,
    );
  }, [showPopup, hidePopup, userRewardReferral?.role, viewPopupDetails]);

  const { availableReward, confirmReward } = useMemo(() => {
    if (userRewardReferral?.isPartnerOrDistributorRole) {
      return {
        availableReward: `${currency}${
          userAccount?.joyCashBalance.available.convertToCurrency(currency) ??
          '0'
        }`,
        confirmReward: `${currency}${
          userAccount?.joyCashBalance.confirmed.convertToCurrency(
            currency,
            userAccount?.joyCashBalance.available,
          ) ?? '0'
        }`,
      };
    } else {
      return {
        availableReward: `${currency}${
          userAccount?.weaCashBalance.available.convertToCurrency(currency) ??
          '0'
        }`,
        confirmReward: `${currency}${
          userAccount?.weaCashBalance.confirmed.convertToCurrency(
            currency,
            userAccount?.weaCashBalance.available,
          ) ?? '0'
        }`,
      };
    }
  }, [
    currency,
    userAccount?.joyCashBalance.available,
    userAccount?.joyCashBalance.confirmed,
    userAccount?.weaCashBalance.available,
    userAccount?.weaCashBalance.confirmed,
    userRewardReferral?.isPartnerOrDistributorRole,
  ]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <WebStoreReferralRewardTemplate
        totalReferralUserCount={userProfile?.totalJoyajoyReferralCount ?? 0}
        profile={userProfile}
        referredByProfile={referredByProfile}
        rewardReferralRole={userRewardReferral?.role}
        userRewardReferralCode={
          userRewardReferral?.isPartnerOrDistributorRole
            ? state.userRewardReferralCode
            : undefined
        }
        availableReward={availableReward}
        confirmReward={confirmReward}
        cashOutRequirement={`${currency}${numbro(
          cashOutRequirements[currency],
        ).format({ thousandSeparated: true })}`}
        referralPartnerData={referralPartnerData}
        referralDistributorData={referralDistributorData}
        referralCustomerData={referralCustomerData}
        onClickReferralPopup={onClickReferralPopup}
        onClickCashInfo={onClickJOYCashInfoPopup}
        onClickRoleDetail={onClickRoleDetail}
        onClickCashOut={onClickCashOut}
        onClickAll={onClickAll}
      />
    </>
  );
};

export default WebStoreReferralRewardScreen;
